import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getAllManagers, createManager, archiveManager } from "../../lib/api";
import type { Manager } from "../../lib/types";

const ID_REGEX = /^[a-z0-9_-]{1,32}$/;

export function ManagersTab() {
  const { t } = useAuth();
  const [list, setList] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const { managers } = await getAllManagers();
      setList(managers);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function submit() {
    setError(null);
    if (!ID_REGEX.test(id)) { setError(t("managers_form_invalid_id")); return; }
    if (!name.trim()) { setError(t("managers_form_need_name")); return; }
    setSubmitting(true);
    try {
      await createManager(id, name.trim());
      setId(""); setName("");
      await reload();
    } catch (e: any) {
      if (e.message === "ID_ALREADY_EXISTS") setError(t("managers_form_dup_id"));
      else if (e.message === "FULL_NAME_ALREADY_EXISTS") setError(t("managers_form_dup_name"));
      else setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggle(m: Manager) {
    if (m.active === 1) {
      const msg = t("managers_archive_confirm", { name: m.full_name });
      if (!window.confirm(msg)) return;
    }
    try {
      await archiveManager(m.id, m.active !== 1);
      await reload();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <section className="p-4 space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <div className="text-sm font-semibold text-slate-800">{t("managers_add_title")}</div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("managers_form_id")}</label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value.toLowerCase())}
            placeholder="petrov"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("managers_form_name")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Петров Иван"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          />
        </div>
        {error && <div className="text-xs text-rose-600">{error}</div>}
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition"
        >{t("managers_form_submit")}</button>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-sm font-semibold text-slate-800 mb-3">{t("managers_list_title")}</div>
        {loading && <div className="text-xs text-slate-500">…</div>}
        {list.length > 0 && (
          <ul className="space-y-2">
            {list.map(m => (
              <li key={m.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <div className={`font-medium ${m.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{m.full_name}</div>
                  <div className="text-[11px] text-slate-500">id: <code>{m.id}</code> {!m.active && <span className="text-rose-500 ml-1">{t("managers_archived_label")}</span>}</div>
                </div>
                <button
                  onClick={() => toggle(m)}
                  className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${m.active ? "bg-amber-100 hover:bg-amber-200 text-amber-800" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800"}`}
                >{m.active ? t("managers_archive") : t("managers_restore")}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
